import React, { Fragment, useState, useEffect, useMemo } from "react";
import { Modal, Button, Form, Badge } from "react-bootstrap";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
  loadSchoolsList,
  loadAssessmentsListBySchoolAssessment,
  loadAssessmentsList,
  loadClassroomsList,
  loadClassroomsStudentList,
  loadUsersList,
} from "../../../actions/admin";
import {
  addAssessmentResult,
  clearAssessmentResult,
  addBulkPMAssessmentResult,
} from "../../../actions/assessment";
import { retrieveSchoolYears } from "../../../actions/admin";
import Loading from "../../layouts/Loading";
import { useHistory } from "react-router-dom";
import MultiSelectWithCheckboxes from "../../layouts/MultiSelect";
import ListGroup from "react-bootstrap/ListGroup";
import {
  ASSESSMENT_TYPE_OPTIONS,
  ALL_LEVEL_OPTIONS,
} from "../../../utils/assessmentToLevelMapping";

const PMAssessmentCreate = ({
  user,
  selectedUser,
  isAuthenticated,
  loadUsersList,
  usersList,
  usersListLoading,
  loadSchoolsList,
  schoolsList,
  schoolsListLoading,
  loadAssessmentsListBySchoolAssessment,
  loadAssessmentsList,
  assessmentsList,
  assessmentListLoading,
  classroomsList,
  classroomListLoading,
  loadClassroomsList,
  loadClassroomsStudentList,
  classroomsStudentList,
  classroomsStudentListLoading,
  accessCode,
  addAssessmentResult,
  addBulkPMAssessmentResult,
  retrieveSchoolYears,
  schoolYears,
  isPMMode = null,
}) => {
  const hist = useHistory();
  const [formData, setFormData] = useState({
    SchoolID:
      selectedUser && selectedUser.SchoolID ? selectedUser.SchoolID : "",
    LevelID: "",
    GradeID: "",
    AssigningGradeID: "",
    StudentID: selectedUser ? selectedUser.UserID : "",
    StudentIDs: [],
    AssessmentTypeID: "",
    AssessmentTypeIDs: [],
    AssessmentID: "",
    AssessmentIDs: [],
    ProbeIDs: [],
    BenchMarkID: "",
    BenchMarkIDs: [],
    ClassroomIDs: [],
    Year: schoolYears?.endYear || new Date().getFullYear() + 1,
  });

  const [grade, setGrade] = useState("");
  const [assigningGrade, setAssigningGrade] = useState("");
  const [school, setSchool] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [active, setActiveStep] = useState(1);

  const probeOptions = useMemo(() => {
    if (!assessmentsList?.length) return [];

    const filteredAssessments = assessmentsList.filter(
      (assessment) =>
        (!formData.SchoolID || assessment.SchoolID === formData.SchoolID) &&
        (formData.LevelID === "" ||
          formData.LevelID === null ||
          formData.LevelID === undefined ||
          assessment.LevelID === formData.LevelID) &&
        (!formData.AssessmentTypeID ||
          assessment.CategoryID === formData.AssessmentTypeID),
    );

    const uniqueProbes = [
      ...new Set(
        filteredAssessments
          .map((assessment) => assessment.ProbeID)
          .filter(Boolean),
      ),
    ];

    return uniqueProbes
      .sort((a, b) => a - b)
      .map((probeId) => ({
        value: probeId,
        label: `Probe ${probeId}`,
      }));
  }, [
    assessmentsList,
    formData.SchoolID,
    formData.LevelID,
    formData.AssessmentTypeID,
  ]);

  // Determine selected assessment type safely
  const selectedAssessment = formData.AssessmentTypeID;
  const isStoryReadingSelected =
    selectedAssessment === 3 ||
    (Array.isArray(selectedAssessment) && selectedAssessment.includes(3));

  const isSillyWordSelected =
    selectedAssessment === 4 ||
    (Array.isArray(selectedAssessment) && selectedAssessment.includes(4));

  // Dynamically filter levels based on selection
  const filteredLevelOptions = isStoryReadingSelected
    ? ALL_LEVEL_OPTIONS.slice(1, 9) // Dynamically grabs only Level 1 and Level 2
    : isSillyWordSelected
      ? ALL_LEVEL_OPTIONS.slice(0, 3) // Dynamically grabs only Level 1, Level 2, and Level 3
      : ALL_LEVEL_OPTIONS.slice(0, 2);

  // Add Focus Handlers
  const handleStepFocus = (step) => {
    setActiveStep(step);
  };

  // Load school list
  useEffect(() => {
    if (user && isAuthenticated) {
      loadSchoolsList();
      loadUsersList({
        SchoolIDs: user.SchoolID
          ? [user.SchoolID]
          : user.schoolIds
            ? [user.schoolIds]
            : [],
      });
      loadClassroomsList();
      loadClassroomsStudentList();
    }
  }, [user]);

  // When user role is teacher, auto set school ID
  useEffect(() => {
    if (
      user &&
      user.SchoolID &&
      user.UserTypeID === 1 &&
      formData.SchoolID === ""
    ) {
      setFormData((prevData) => ({
        ...prevData,
        SchoolID: user.SchoolID,
      }));
    }
  }, [user, formData.SchoolID]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      Year: schoolYears?.endYear || new Date().getFullYear() + 1,
    }));
  }, [schoolYears]);

  useEffect(() => {
    if (!schoolYears || !schoolYears.endYear) {
      retrieveSchoolYears(new Date());
    }
  }, [schoolYears, retrieveSchoolYears]);

  // Load Assessment Belongs to the School
  useEffect(() => {
    if (formData.SchoolID !== "") {
      loadAssessmentsListBySchoolAssessment(formData.SchoolID);
    }
  }, [formData.SchoolID]);

  // Cross-filter logic
  // Assessments
  useEffect(() => {
    if (!assessmentsList || assessmentsList.length === 0) return [];

    const res = assessmentsList.filter((assessment) => {
      // Filter by school if selected
      const schoolMatch =
        !formData.SchoolID ||
        formData.SchoolID === "" ||
        assessment.SchoolID === formData.SchoolID;

      const levelMatch =
        formData.LevelID === "" ||
        formData.LevelID === null ||
        formData.LevelID === undefined ||
        assessment.LevelID === formData.LevelID;

      // Filter by assessment types if selected
      const assessmentTypeMatch =
        !formData.AssessmentTypeID ||
        formData.AssessmentTypeID === "" ||
        assessment.CategoryID === formData.AssessmentTypeID;

      const probeMatch =
        !formData.ProbeIDs ||
        formData.ProbeIDs.length === 0 ||
        formData.ProbeIDs.includes(assessment.ProbeID);

      return schoolMatch && levelMatch && assessmentTypeMatch && probeMatch;
    });

    const assessmentIDs = res.map((assessment) => assessment.AssessmentID);
    setFormData((prev) => ({
      ...prev,
      AssessmentIDs: assessmentIDs,
    }));
  }, [
    assessmentsList,
    formData.SchoolID,
    formData.LevelID,
    formData.AssessmentTypeIDs,
    formData.ProbeIDs,
  ]);

  const validateForm = () => {
    if (formData.SchoolID === "") {
      setWarningMessage("School is required");
      setShowWarning(true);
      return false;
    }

    if (!formData.Year || formData.Year === "") {
      setWarningMessage("Year is required");
      setShowWarning(true);
      return false;
    }
    if (
      formData.LevelID === "" ||
      formData.LevelID === null ||
      formData.LevelID === undefined
    ) {
      setWarningMessage("Assigning Level is required");
      setShowWarning(true);
      return false;
    }

    if (!formData.AssessmentTypeID || formData.AssessmentTypeID === "") {
      setWarningMessage("Assessment Type is required");
      setShowWarning(true);
      return false;
    }

    if (!formData.ProbeIDs || formData.ProbeIDs.length === 0) {
      setWarningMessage("At least one probe must be selected");
      setShowWarning(true);
      return false;
    }

    if (!formData.AssessmentIDs || formData.AssessmentIDs.length === 0) {
      setWarningMessage("No matching assessment found.");
      setShowWarning(true);
      return false;
    }

    return true;
  };

  // Add this function to handle the confirmation
  const handleConfirmAssignment = async () => {
    try {
      const res = await addBulkPMAssessmentResult(formData);

      if (res && res.length) {
        const msgStr = res.map((msg) => msg).join("\n");
        setWarningMessage(msgStr);
        setShowWarning(true);
        return;
      }

      // Reset form but keep school context for the next assignment.
      setFormData({
        SchoolID:
          formData.SchoolID || selectedUser?.SchoolID || user?.SchoolID || "",
        LevelID: formData.LevelID ?? "",
        GradeID: "",
        AssigningGradeID: "",
        StudentID: selectedUser?.UserID || "",
        StudentIDs: [],
        AssessmentTypeID: formData.AssessmentTypeID || "",
        AssessmentTypeIDs: [],
        AssessmentID: "",
        AssessmentIDs: [],
        ProbeIDs: [],
        BenchMarkID: "",
        BenchMarkIDs: [],
        ClassroomIDs: [],
        Year: schoolYears?.endYear || new Date().getFullYear() + 1,
      });
      setShowConfirmModal(false);
    } catch (error) {
      console.error("Error creating assessment assignments:", error);
      setWarningMessage("An error occurred while creating assignments.");
      setShowWarning(true);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();

    // Validate form data
    if (!validateForm()) {
      return;
    }

    setShowConfirmModal(true);
  };

  const onChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const {
    StudentID,
    StudentIDs,
    AssessmentID,
    AssessmentTypeID,
    BenchMarkID,
    Year,
  } = formData;

  return (
    <Fragment>
      {user !== null && isAuthenticated && !usersListLoading ? (
        <div className=" container mx-auto">
          <div className="d-flex  position-relative ">
            <div className="container card shadow border-0 w-100 ">
              <div className="card-body  mt-1">
                <form onSubmit={(e) => onSubmit(e)}>
                  <ListGroup>
                    {/* Basic Information Section */}
                    <ListGroup.Item className="p-0">
                      {/* Section Header & Description */}
                      <div
                        className={`${
                          active === 1 && "bg-primary text-white"
                        } px-3 py-1 border-bottom`}
                      >
                        <h6 className="mt-2">Assign Progress Monitoring</h6>
                        <p className="mt-2">
                          Please select the assessment(s) and its configuration.
                        </p>
                      </div>

                      {/* Section Details */}

                      {/* Assessment Type */}
                      <div className="row g-3 p-3 mb-3">
                        {/* Assessment Type */}
                        {/* Only show Assessment Type selection for certain user types (SuperAdmin, School Admin, Teacher) */}
                        {(user.UserTypeID === 5 ||
                          user.UserTypeID === 6 ||
                          user.UserTypeID === 1) &&
                          !schoolsListLoading && (
                            <div
                              className="col-md-12 mt-3"
                              onFocus={() => handleStepFocus(2)}
                            >
                              <label htmlFor="AssessmentTypeID">
                                <b>Assessment Type</b>
                              </label>
                              <MultiSelectWithCheckboxes
                                id="AssessmentTypeID"
                                value={formData.AssessmentTypeID || []}
                                optionsList={ASSESSMENT_TYPE_OPTIONS}
                                placeholder={"Select an Assessment Type"}
                                formData={formData}
                                setFormData={setFormData}
                                isMulti={false}
                                showSelectAll={false}
                              />
                            </div>
                          )}
                      </div>

                      {/* Assigning Level */}
                      <div className="row g-3 p-3 mb-3">
                        {/* Probe */}
                        {(user.UserTypeID === 5 ||
                          user.UserTypeID === 6 ||
                          user.UserTypeID === 1) &&
                          !schoolsListLoading && (
                            <div
                              className="col-md-12 mt-3"
                              onFocus={() => handleStepFocus(1)}
                            >
                              <label for="LevelID">
                                <b>Assigning Level</b>
                              </label>
                              <MultiSelectWithCheckboxes
                                id="LevelID"
                                value={formData.LevelID ?? []}
                                optionsList={filteredLevelOptions}
                                placeholder={"Select a Level"}
                                formData={formData}
                                setFormData={setFormData}
                                isMulti={false}
                                showSelectAll={false}
                              />
                            </div>
                          )}
                      </div>

                      {/* Assessment Probe */}
                      <div className="row g-3 p-3 mb-3">
                        {/* Probe */}
                        {(user.UserTypeID === 5 ||
                          user.UserTypeID === 6 ||
                          user.UserTypeID === 1) &&
                          !schoolsListLoading && (
                            <div
                              className="col-md-12 mt-3"
                              onFocus={() => handleStepFocus(1)}
                            >
                              <label for="ProbeIDs">
                                <b>Assessment Probe</b>
                              </label>
                              <MultiSelectWithCheckboxes
                                id="ProbeIDs"
                                value={formData.ProbeIDs || []}
                                optionsList={probeOptions}
                                placeholder={"Select Probe(s)"}
                                formData={formData}
                                setFormData={setFormData}
                                isMulti={true}
                                showSelectAll={true}
                              />
                            </div>
                          )}
                      </div>
                    </ListGroup.Item>
                  </ListGroup>

                  {/* Action Area */}
                  <div className="d-flex align-items-center justify-content-end my-3">
                    <Button
                      type="submit"
                      size="sm"
                      className={`button-primary px-5`}
                    >
                      Assign
                    </Button>
                  </div>
                  {/* <div className="text-center my-2">
                    Use this{" "}
                    <a
                      href={`https://www.academicassessments.ca/assessment/join`}
                      className="txt-primary"
                    >
                      https://www.academicassessments.ca/assessment/join
                    </a>{" "}
                    to enter the Access Code and start the assessment.
                  </div>
                  {accessCode !== null && (
                    <div className="text-center my-2">
                      <h5 className="text-center">Access Code: {accessCode}</h5>
                    </div>
                  )} */}
                </form>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="d-flex align-items-center h-100 justify-content-center">
          <Loading />
        </div>
      )}

      {/* Modal: Confirm assign assessment */}
      {/* React Bootstrap Modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Assessment Assignment</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="mb-3">
            <p className="text-muted">
              Please review the assessment details before confirming the
              assignment.
            </p>
          </div>

          {/* School Information */}
          {formData.SchoolID && (
            <div className="mb-3">
              <Form.Label>
                <strong>School</strong>
              </Form.Label>
              <div className="p-2 bg-light rounded">
                {user?.UserTypeID === 5
                  ? schoolsList?.find(
                      (school) =>
                        school.SchoolID === parseInt(formData.SchoolID),
                    )?.Name || "Unknown School"
                  : user?.UserTypeID === 6
                    ? usersList?.find(
                        (school) =>
                          school.SchoolID === parseInt(formData.SchoolID),
                      )?.SchoolName || "Unknown School"
                    : user?.UserTypeID === 1
                      ? usersList?.find(
                          (school) =>
                            school.SchoolID === parseInt(formData.SchoolID),
                        )?.SchoolName || "Unknown School"
                      : "N/A"}
              </div>
            </div>
          )}

          {/* Selected Assessments */}
          <div className="mb-3">
            <Form.Label>
              <strong>
                Selected Assessments ({formData.AssessmentIDs.length})
              </strong>
            </Form.Label>
            <div
              className="p-2 bg-light rounded"
              style={{ maxHeight: "200px", overflowY: "auto" }}
            >
              {formData.AssessmentIDs.length > 0 ? (
                <div className="d-flex flex-wrap gap-1">
                  {formData.AssessmentIDs?.map((assessmentId) => {
                    const assessment = assessmentsList?.find(
                      (a) => a.AssessmentID === assessmentId.split("/")[0],
                    );
                    return assessment ? (
                      <span
                        key={assessmentId}
                        className="badge me-1 mb-1"
                        style={{
                          backgroundColor: "#18a587",
                          color: "#ffffff",
                          fontSize: "0.875rem",
                          fontWeight: "600",
                          padding: "0.5rem 0.75rem",
                          borderRadius: "0.375rem",
                        }}
                        role="status"
                        aria-label={`Assessment: ${assessment.Title} in category ${assessment.Category}`}
                      >
                        {assessment.Title} ({assessment.Category})
                      </span>
                    ) : null;
                  })}
                </div>
              ) : (
                <span className="text-muted">No assessments selected</span>
              )}
            </div>
          </div>

          {/* Warning Message */}
          <div className="alert alert-warning mt-4">
            <strong>⚠️ Important:</strong> Once assigned, the assessment cannot
            be changed.
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmModal(false)}
          >
            Cancel
          </Button>
          <Button className="button-primary" onClick={handleConfirmAssignment}>
            Confirm Assignment
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Warning Modal */}
      {showWarning && warningMessage && (
        <Modal show={showWarning} onHide={() => setShowWarning(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Warning</Modal.Title>
          </Modal.Header>
          <Modal.Body className="d-flex flex-column gap-2">
            {warningMessage.split("\n").map((line, idx) => (
              <div key={idx}>{line}</div>
            ))}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowWarning(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Fragment>
  );
};

PMAssessmentCreate.propTypes = {
  user: PropTypes.object,
  selectedUser: PropTypes.object,
  isAuthenticated: PropTypes.bool.isRequired,
  addAssessmentResult: PropTypes.func.isRequired,
  addBulkPMAssessmentResult: PropTypes.func.isRequired,
};
const mapStateToProps = (state) => ({
  user: state.auth.user,
  selectedUser: state.admin.user,
  isAuthenticated: state.auth.isAuthenticated,
  usersList: state.admin.usersList,
  usersListLoading: state.admin.usersListLoading,
  schoolsList: state.admin.schoolsList,
  schoolsListLoading: state.admin.schoolsListLoading,
  assessmentsList: state.admin.assessmentsList,
  assessmentListLoading: state.admin.assessmentListLoading,
  classroomsList: state.admin.classroomsList,
  classroomListLoading: state.admin.classroomListLoading,
  classroomsStudentList: state.admin.classroomsStudentList,
  classroomsStudentListLoading: state.admin.classroomsStudentListLoading,
  accessCode: state.assessment.accessCode,
  clearAdmin: state.admin.clearAdmin,
  schoolYears: state.admin.schoolYears,
});

export default connect(mapStateToProps, {
  loadUsersList,
  loadSchoolsList,
  loadAssessmentsListBySchoolAssessment,
  loadAssessmentsList,
  addAssessmentResult,
  addBulkPMAssessmentResult,
  loadClassroomsList,
  loadClassroomsStudentList,
  retrieveSchoolYears,
})(PMAssessmentCreate);
